import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { http_get, http_post, http_put } from '../utils/api_client';
import { API_ENDPOINTS } from '../utils/endpoints';

// BL-090 — the builder's Distribution panel. Two modes:
//   "Send to people"  -> invite specific audience members (POST .../distribute),
//                        which also silently grants access to the checklist items.
//   "Make available"  -> set link visibility (public / guest / invite-only) and
//                        copy the shareable learner URL.
const VIS = [
  { key: 'public', label: 'Anyone with the link', hint: 'No sign-in required.' },
  { key: 'guest', label: 'Require name & email', hint: 'Collect details before starting.' },
  { key: 'private', label: 'Invite only', hint: 'Only people you send it to.' }
];

const ChecklistDistribution = ({ checklistId, slug, companySlug, initialVisibility = 'private' }) => {
  const [mode, setMode] = useState('people');

  // Send-to-people state
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]); // [{id, name, email}]
  const [sendEmail, setSendEmail] = useState(true);
  const [autoRemind, setAutoRemind] = useState(true);
  const [sending, setSending] = useState(false);

  // Make-available state
  const [visibility, setVisibility] = useState(initialVisibility || 'private');
  const [savingVis, setSavingVis] = useState(false);

  const shareUrl = `${window.location.origin}/${companySlug}/hub/${slug}`;

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (mode !== 'people') return undefined;
    let cancelled = false;
    http_get(API_ENDPOINTS.GET_AUDIENCES, { keyword: debounced, limit: 8 })
      .then(({ data }) => { if (!cancelled) setResults(data.records || []); })
      .catch(() => { if (!cancelled) setResults([]); });
    return () => { cancelled = true; };
  }, [mode, debounced]);

  const selectedIds = useMemo(() => new Set(selected.map((s) => s.id)), [selected]);

  const addPerson = (a) => {
    if (selectedIds.has(a.id)) return;
    setSelected((prev) => [...prev, { id: a.id, name: a.learner?.name || a.learner?.email || 'Learner', email: a.learner?.email }]);
    setSearch('');
  };
  const removePerson = (id) => setSelected((prev) => prev.filter((s) => s.id !== id));

  const send = async () => {
    if (selected.length === 0) return;
    setSending(true);
    try {
      const { data, status } = await http_post(API_ENDPOINTS.DISTRIBUTE_CHECKLIST(slug), {
        audience_ids: selected.map((s) => s.id),
        send_branded_email: sendEmail,
        auto_remind: autoRemind
      });
      if (data?.error || status >= 300) { toast.error(data.error || 'Could not send.'); return; }
      const created = (data.created || []).length;
      const skipped = (data.skipped || []).length;
      toast.success(`Sent to ${created} ${created === 1 ? 'person' : 'people'}`);
      if (skipped) toast.info(`${skipped} already had access`);
      setSelected([]);
    } catch (e) {
      toast.error('Something went wrong.');
    } finally {
      setSending(false);
    }
  };

  const saveVisibility = async (next) => {
    setVisibility(next);
    setSavingVis(true);
    try {
      await http_put(API_ENDPOINTS.PUT_LEARNING_MATERIAL_VISIBILITY(checklistId), {
        visibility: next,
        material_type: 'Checklists'
      });
      toast.success('Link access updated');
    } catch (e) {
      toast.error('Could not update link access');
    } finally {
      setSavingVis(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(shareUrl).then(() => toast.success('Link copied'), () => {});
  };

  return (
    <div className="cl-settle">
      <div className="cl-settle__h">Distribution</div>
      <div className="cl-settle__b">
        <div className="cl-modeseg">
          <div className={`s ${mode === 'people' ? 'on' : ''}`} onClick={() => setMode('people')}>Send to people</div>
          <div className={`s ${mode === 'link' ? 'on' : ''}`} onClick={() => setMode('link')}>Make available</div>
        </div>

        {mode === 'people' ? (
          <>
            <p className="cl-lab">Recipients</p>
            <div className="cl-chipbox">
              {selected.map((s) => (
                <span key={s.id} className="cl-chip">{s.name}<b onClick={() => removePerson(s.id)}>✕</b></span>
              ))}
              {selected.length === 0 && <span className="cl-chip-empty">No one selected yet</span>}
            </div>
            <input
              className="cl-miniinput"
              placeholder="Search audience by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && results.length > 0 && (
              <div className="cl-results">
                {results.map((a) => (
                  <div key={a.id} className={`cl-result ${selectedIds.has(a.id) ? 'is-added' : ''}`} onClick={() => addPerson(a)}>
                    <span className="nm">{a.learner?.name || a.learner?.email}</span>
                    <span className="add">{selectedIds.has(a.id) ? 'Added' : '+ Add'}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="cl-togrow" onClick={() => setSendEmail((v) => !v)}>
              Email them an invite <span className={`cl-sw ${sendEmail ? 'on' : ''}`} />
            </div>
            <div className="cl-togrow" onClick={() => setAutoRemind((v) => !v)}>
              Remind if not started in 3 days <span className={`cl-sw ${autoRemind ? 'on' : ''}`} />
            </div>

            <button type="button" className="cl-btn cl-btn--primary cl-send" disabled={selected.length === 0 || sending} onClick={send}>
              {sending ? 'Sending…' : `Send to ${selected.length || ''} ${selected.length === 1 ? 'person' : 'people'}`.trim()}
            </button>
          </>
        ) : (
          <>
            <p className="cl-lab">Shareable link</p>
            <div className="cl-linkbox">
              <span className="cl-linkbox__u">{shareUrl}</span>
              <span className="cl-linkbox__cp" onClick={copyLink}>Copy</span>
            </div>

            <p className="cl-lab" style={{ marginTop: 12 }}>Who can open it</p>
            {VIS.map((v) => (
              <div key={v.key} className={`cl-visrow ${visibility === v.key ? 'on' : ''}`} onClick={() => saveVisibility(v.key)}>
                <span className={`cl-radio ${visibility === v.key ? 'on' : ''}`} />
                <div>
                  <div className="cl-visrow__t">{v.label}</div>
                  <div className="cl-visrow__h">{v.hint}</div>
                </div>
              </div>
            ))}
            {savingVis && <div className="cl-savinghint">Saving…</div>}
            {visibility !== 'private' && (
              <div className="cl-visnote">
                Heads up: private paths or walkthroughs inside this checklist won’t open from a shared link. Use “Send to people” to grant item access, or make those items public.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChecklistDistribution;
